import { Component, Input } from "@angular/core";
import { ImageComponent } from "../../../../models/report-preview/TableData";
import { ExifOrientation } from "../../../../../app.constants";
import { OfflineReportPreviewService } from "../../../../../_services/offline-report-preview.service";
import { ReportDataPlate } from "../../../../models/report-preview/report-data-plate";

@Component({
  selector: "app-report-column-image",
  templateUrl: "./report-column-image.component.html",
  styleUrls: ["./report-column-image.component.scss"],
  standalone: false
})
export class ReportColumnImageComponent {
  @Input() image: ImageComponent;
  @Input() isColumnStyle: boolean;
  @Input() sizeUnit: string;
  @Input() width: number;
  @Input() height: number;
  @Input() maxWidth: number;
  @Input() maxHeight: number;
  @Input() isAbsolute = false;
  @Input() top = 0;
  @Input() left = 0;
  @Input() minWidth = 0;
  @Input() isItemsHorizontal = false;
  @Input() columnCount = 1;
  @Input() imageCount = 1;
  @Input() horizontalAlign = "center";
  @Input() verticalAlign = "middle";
  @Input() pictureKey: string;
  @Input() widthRatio: string;
  @Input() reportDataPlate: ReportDataPlate;

  get isSaveHtml() {
    return this.offlineReportPreviewService.isSaveHtml;
  }

  get exifOrientation() {
    return ExifOrientation;
  }

  private picturePageBreakRow: number;
  readonly mirrorHorizontalAndRotate270 = 5;
  readonly columnStyle = "height: 100%; ";
  readonly divStyle = "display: block; word-break: normal; white-space: pre-wrap;";
  readonly dummyStyle =
    " display: block; overflow: hidden; word-break: normal; white-space: pre-wrap;" +
    " max-width: initial; max-height: initial; font-size: 7mm; text-align: center;";

  constructor(
    public designService: OfflineReportPreviewService,
    private offlineReportPreviewService: OfflineReportPreviewService
  ) {
    this.picturePageBreakRow = designService.getpicturePageBreakRow();
  }

  getAreaStyle() {
    let obj = {};
    const horizontalAlign = this.horizontalAlign;
    const verticalAlign = this.verticalAlign;
    obj["height"] = this.imageCount > 1 || this.columnCount > 1 ? "100%" : `${this.maxHeight}${this.sizeUnit}`;
    obj = this.getOrientationStyle(obj, this.image);
    obj = this.getFlexStyle(obj, horizontalAlign, verticalAlign);
    return obj;
  }

  getSize(pattern: string) {
    const summary = this.designService.getSummary(this.pictureKey);
    const tableWidth = parseInt(summary.tableWidth);
    const tableHeight = parseInt(summary.tableHeight);

    const tableHeaderHeight = parseInt(summary.tableHeaderHeight);
    const widthRatio = parseInt(this.widthRatio);

    let imageWidth = tableWidth * (widthRatio / 100);
    let imageHeight = (tableHeight - tableHeaderHeight) / this.picturePageBreakRow;

    while (imageWidth > this.maxWidth || imageHeight > this.maxHeight) {
      const ratio = this.getRatio(imageWidth, imageHeight, this.maxWidth, this.maxHeight);
      imageWidth *= ratio;
      imageHeight *= ratio;
    }

    if (pattern === "width") {
      return imageWidth + "mm";
    } else {
      return imageHeight + "mm";
    }
  }

  getOrientationStyle(obj: any, image: ImageComponent) {
    if ((5 <= image.orientation && image.orientation <= 8) || (image.usedImageMode === 1 && image.modifiedHeight > image.modifiedWidth)) {
      const minWidth = this.isItemsHorizontal ? this.minWidth + this.sizeUnit : "";
      obj["min-width"] = `${minWidth}`;

      return obj;
    }
    return obj;
  }

  clickImage(imageId: string) {
    top.postMessage(
      {
        action: "EditImage",
        message: imageId
      },
      "*"
    );
  }

  getImageStyle(image: ImageComponent) {
    const isModified = image.usedImageMode === 1;
    const obj = {};

    let imageWidth = this.width;
    let imageHeight = this.height;
    let imageMaxWidth = this.reportDataPlate ? this.reportDataPlate.imageInfo.image.width : this.maxWidth;
    const imageMaxHeight = this.maxHeight;

    if (!isModified && image.orientation >= ExifOrientation.MirrorHorizontalAndRotate270 && imageHeight !== imageWidth) {
      imageWidth = this.height;
      imageHeight = this.width;
    }

    while (imageWidth > imageMaxWidth || imageHeight > imageMaxHeight) {
      const ratio = this.getRatio(imageWidth, imageHeight, imageMaxWidth, imageMaxHeight);
      imageWidth *= ratio;
      imageHeight *= ratio;
    }

    obj[`cursor`] = "pointer";
    obj[`max-width.${this.sizeUnit}`] = this.reportDataPlate ? this.maxWidth : imageMaxWidth;
    obj[`max-height.${this.sizeUnit}`] = imageMaxHeight;

    if (this.columnCount === 1) {
      imageMaxWidth = this.reportDataPlate ? this.maxWidth : imageMaxWidth; // DataPlate画像の場合、横幅の計算方法がPicture tableとは異なるのでWidthの値を入れ替える必要がある
      if (Number(imageMaxHeight?.toFixed(2)) > Number(imageHeight?.toFixed(2))) {
        const h = imageMaxHeight - imageHeight;
        obj[`margin-top.${this.sizeUnit}`] = h / 2;
        obj[`margin-bottom.${this.sizeUnit}`] = h / 2;
      }

      if (Number(imageMaxWidth?.toFixed(2)) > Number(imageWidth?.toFixed(2))) {
        const w = imageMaxWidth - imageWidth;
        obj[`margin-left.${this.sizeUnit}`] = w / 2;
        obj[`margin-right.${this.sizeUnit}`] = w / 2;
      }
      // ToDo ライブラリを変更しても寄せが効かなかった場合、下記のように画像にmarginを入れて寄せ対応する必要がある
      /* const horizontalAlign = this.horizontalAlign ?? 'center';
        const verticalAlign = this.verticalAlign ?? 'middle';
        if (Number(imageMaxHeight.toFixed(2)) > Number(imageHeight.toFixed(2))) {
        const h = imageMaxHeight - imageHeight;
        if (verticalAlign !== 'top') {
          obj[`margin-top.${this.sizeUnit}`] = verticalAlign === 'middle' ? h / 2 : h;
        }
        if (verticalAlign !== 'bottom') {
          obj[`margin-bottom.${this.sizeUnit}`] = verticalAlign === 'middle' ? h / 2 : h;
        }
      }

      if (Number(imageMaxWidth.toFixed(2)) > Number(imageWidth.toFixed(2))) {
        const w = imageMaxWidth - imageWidth;
        if (horizontalAlign !== 'left') {
          obj[`margin-left.${this.sizeUnit}`] = horizontalAlign === 'center' ? w / 2 : w;
        }
        if (horizontalAlign !== 'right') {
          obj[`margin-right.${this.sizeUnit}`] = horizontalAlign === 'center' ? w / 2 : w;
        }
      } */
    }

    obj[`top.${this.sizeUnit}`] = this.top;
    obj[`left.${this.sizeUnit}`] = this.left;
    if (this.isAbsolute) {
      obj["position"] = "absolute";
    }

    return obj;
  }

  getRatio(width: number, height: number, maxWidth: number, maxHeight: number) {
    if (width > maxWidth && height <= maxHeight) {
      return maxWidth / width;
    } else if (width <= maxWidth && height > maxHeight) {
      return maxHeight / height;
    } else {
      if (maxWidth > maxHeight) {
        return maxHeight / height;
      } else {
        return maxWidth / width;
      }
    }
  }

  getFlexStyle(obj: any, horizontalAlign = "center", verticalAlign = "middle") {
    switch (horizontalAlign) {
      case "left":
        horizontalAlign = "start";
        break;
      case "center":
        horizontalAlign = "center";
        break;
      case "right":
        horizontalAlign = "end";
        break;
    }
    switch (verticalAlign) {
      case "top":
        verticalAlign = "start";
        break;
      case "middle":
        verticalAlign = "center";
        break;
      case "bottom":
        verticalAlign = "end";
        break;
    }

    obj["display"] = "flex;";
    obj["align-items"] = `${verticalAlign}; `;
    obj["-webkit-box-align"] = `${verticalAlign}; `;
    obj["-webkit-align-items"] = `${verticalAlign}; `;
    obj["-ms-flex-align"] = `${verticalAlign}; `;
    obj["justify-content"] = `${horizontalAlign}; `;
    obj["-webkit-box-pack"] = `${horizontalAlign}; `;
    obj["-webkit-justify-content"] = `${horizontalAlign}; `;
    obj["-ms-flex-pack"] = `${horizontalAlign}; `;
    return obj;
  }
}
